import { WizardInfoViewModel, SiteWizardViewModel, WizardDeviceInfoViewModel, WizardRegionInfoViewModel, WizardClassifierInfoViewModel, WizardCategorySystemInfoViewModel, WizardVpsMeasurementsInfoViewModel, WizardFpsOptionsInfoViewModel } from "@/TemsManagerWebInterface"
import Localization from "@/Localization"
import { WizardMode } from "./WizardView"
import { WizardFpsSetup } from "@/TemsManagerWebEnums";

export interface WizardValidityStates {
    Locations: boolean,
    Components: boolean,
    AxleDetection: boolean,
    FpsOptions: boolean,
    Region: boolean,
    Classifier: boolean,
    VpsMeasurements: boolean,
}

/// Methods used for validation of wizard settings
export class WizardValidator {

    // validation of road settings
    public static Locations(domElement: JQuery,
        validityStates: WizardValidityStates,
        wizardModel: SiteWizardViewModel,
        infoModel: WizardInfoViewModel): string {

        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");


        healthState.addClass("glyphicon-ok good");
        validityStates.Locations = true;
        let road = wizardModel.LocationId ?
            infoModel.Locations.filter((value) => { return value.Id == wizardModel.LocationId; })[0] :
            null;

        return road ?
            Localization.resourceStrings.SiteWizardModal_EditRoadsReplaceRoadTitle.replace("{0}", road.Title).replace("{1}", road.SequenceNumber.toString()) :
            Localization.resourceStrings.SiteWizardModal_EditRoadsNewRoadTitle;
    }

    // validation of component settings
    public static Components(domElement: JQuery,
        validityStates: WizardValidityStates,
        wizardModel: SiteWizardViewModel,
        infoModel: WizardInfoViewModel,
        captureDeviceTypeIds: string[]): string {

        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");

        let activeDeviceTypes: number = 0;
        let maxDeviceGroupCount: number = 0;

        if (wizardModel.CapturingDevices == null) {
            // no devices (wizard model is empty)
            validityStates.Components = false;
            return Localization.resourceStrings.SiteWizardModal_NotConfigured;
        }

        for (let deviceTypeId of captureDeviceTypeIds) {
            let deviceGroup = wizardModel.CapturingDevices
                .filter(d => { return d.DeviceType.Id === deviceTypeId; });
            let currentCount: number = 0;
            for (let device of deviceGroup) {
                currentCount += device.Count;
            }

            maxDeviceGroupCount = currentCount > maxDeviceGroupCount ? currentCount : maxDeviceGroupCount;

            if (currentCount > 0) {
                activeDeviceTypes++;
            }
        }

        if (activeDeviceTypes === 0) {
            // at least one capturing device has to be chosen
            validityStates.Components = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorNoComponentChosen;
        } else if (activeDeviceTypes > 1) {
            // only one type of device is allowed in simple view
            healthState.addClass("glyphicon-remove bad");
            validityStates.Components = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorMultipleComponentTypes;
        } else if (maxDeviceGroupCount > infoModel.MaxDevicesCount) {
            // not more than four devices are allowed in simple view
            healthState.addClass("glyphicon-remove bad");
            validityStates.Components = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorTooManyComponents
                .replace("{0}", wizardModel.CapturingDevices[0].Name)
                .replace("{1}", infoModel.MaxDevicesCount.toString());
        } else {
            healthState.addClass("glyphicon-ok good");
            validityStates.Components = true;
            return this.GetDeviceHtmlString(wizardModel.CapturingDevices);
        }
    }

    // validation of axle detection settings
    public static AxleDetection(domElement: JQuery,
        validityStates: WizardValidityStates,
        wizardModel: SiteWizardViewModel,
        currentRoadType: string): string {

        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");

        if (wizardModel.AxleDetectionDevices == null) {

            validityStates.AxleDetection = false;
            return Localization.resourceStrings.SiteWizardModal_NotConfigured;
        }

        if (!validityStates.Components) {

            validityStates.AxleDetection = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorComponentFieldEmpty;
        }

        for (let device of wizardModel.AxleDetectionDevices) {
            if (device.Count < 0 || device.Count > device.MaxCount) {
                healthState.addClass("glyphicon-remove bad");
                validityStates.AxleDetection = false;
                return Localization.resourceStrings.SiteWizardModal_ErrorTooManyComponents
                    .replace("{0}", device.Title).replace("{1}", device.MaxCount.toString());
            }
        }

        healthState.addClass("glyphicon-ok good");
        validityStates.AxleDetection = true;
        return this.GetDeviceHtmlString(wizardModel.AxleDetectionDevices
            .filter((device) => { return device.LocationTypeName === currentRoadType; }));
    }

    // creates a readable string of devices with name and count
    private static GetDeviceHtmlString(devices: WizardDeviceInfoViewModel[]): string {

        let resultString: string = "";
        for (let device of devices) {
            if (device.Count > 0) {
                if (resultString.length > 0) {
                    resultString += ", ";
                }

                resultString += `${device.Title}: ${device.Count}`;
            }
        }

        return resultString;
    }

    // validation of axle detection settings
    public static FpsOptions(domElement: JQuery,
        validityStates: WizardValidityStates,
        currentFpsOptionsInfo: WizardFpsOptionsInfoViewModel): string {

        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");

        validityStates.FpsOptions = currentFpsOptionsInfo !== null;
        if (validityStates.FpsOptions) {
            healthState.addClass("glyphicon-ok good");
            switch (currentFpsOptionsInfo.FpsSetup) {
                case WizardFpsSetup.SingleLane:
                    return Localization.resourceStrings.SiteWizardModal_EnumFpsSetupSingleLane;
                case WizardFpsSetup.SingleLaneWithAC:
                    return Localization.resourceStrings.SiteWizardModal_EnumFpsSetupSingleLaneWithAC;
                case WizardFpsSetup.DoubleLaneShared:
                    return Localization.resourceStrings.SiteWizardModal_EnumFpsSetupDoubleLane;
            }

            return WizardFpsSetup[currentFpsOptionsInfo.FpsSetup];
        }

        return Localization.resourceStrings.SiteWizardModal_NotConfigured;
    }

    // validation of region settings
    public static Region(domElement: JQuery,
        validityStates: WizardValidityStates,
        selectedRegionInfo: WizardRegionInfoViewModel,
        mode: WizardMode): string {

        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");
        if (selectedRegionInfo == null) {

            validityStates.Region = false;
            return Localization.resourceStrings.SiteWizardModal_NotConfigured;
        }

        if (!validityStates.Components) {

            validityStates.AxleDetection = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorComponentFieldEmpty;
        }

        if (mode === WizardMode.Tic && !validityStates.AxleDetection) {

            validityStates.Region = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorAxleDetectionFieldEmpty;
        }

        healthState.addClass("glyphicon-ok good");
        validityStates.Region = true;
        return selectedRegionInfo != null ? selectedRegionInfo.Title : null;
    }

    // validation of classifier settings
    public static Classifier(domElement: JQuery,
        validityStates: WizardValidityStates,
        selectedClassifierInfo: WizardClassifierInfoViewModel,
        mode: WizardMode): string {

        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");

        if (selectedClassifierInfo == null) {

            validityStates.Classifier = false;
            return Localization.resourceStrings.SiteWizardModal_NotConfigured;
        }

        if (!validityStates.Components) {

            validityStates.AxleDetection = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorComponentFieldEmpty;
        }

        if (mode === WizardMode.Tic && !validityStates.AxleDetection) {

            validityStates.Region = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorAxleDetectionFieldEmpty;
        }

        if (!validityStates.Region) {

            validityStates.Region = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorRegionFieldEmpty;
        }

        healthState.addClass("glyphicon-ok good");
        validityStates.Classifier = true;
        return selectedClassifierInfo != null ? selectedClassifierInfo.Title : null;
    }

    public static CategorySystem(validityStates: WizardValidityStates,
        selectedCategorySystem: WizardCategorySystemInfoViewModel): string {

        if (!validityStates.Components || !validityStates.AxleDetection || !validityStates.Region) {
            return "";
        }

        if (!validityStates.Classifier) {
            return Localization.resourceStrings.SiteWizardModal_ErrorClassifierFieldEmpty;
        }

        return selectedCategorySystem != null ? selectedCategorySystem.Title : null;
    }

    // validation of measurement settings
    public static VpsMeasurements(domElement: JQuery,
        validityStates: WizardValidityStates,
        currentVpsMeasurements: WizardVpsMeasurementsInfoViewModel): string {
        let healthState: JQuery = domElement.find("span.wizard-state");
        healthState.removeClass("glyphicon-ok glyphicon-remove good warning bad");

        if (currentVpsMeasurements == null) {
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_NotConfigured;
        }

        if (!validityStates.Components) {
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorComponentFieldEmpty;
        }

        if (!validityStates.Region) {
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorRegionFieldEmpty;
        }

        if (currentVpsMeasurements.LmsDistanceWidth < currentVpsMeasurements.LmsDistanceWidthMin) {
            healthState.addClass("glyphicon-remove bad");
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorMeasurementsWidthRoad
                .replace(/\{0\}/g, currentVpsMeasurements.LmsDistanceWidthMin.toFixed(2));
        }

        if (currentVpsMeasurements.LmsDistanceWidth < currentVpsMeasurements.VehicleMaxWidth) {
            healthState.addClass("glyphicon-remove bad");
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorMeasurementsWidth;
        }

        if (currentVpsMeasurements.LmsHeightWidth < currentVpsMeasurements.VehicleMaxHeight) {
            healthState.addClass("glyphicon-remove bad");
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorMeasurementsHeight;
        }

        if (currentVpsMeasurements.LmsDistanceLength < currentVpsMeasurements.VehicleMaxLength) {
            healthState.addClass("glyphicon-remove bad");
            validityStates.VpsMeasurements = false;
            return Localization.resourceStrings.SiteWizardModal_ErrorMeasurementsLength;
        }

        var settingsString = Localization.resourceStrings.SiteWizardModal_SummaryVpsMeasurementsFormat
            .replace(/\{0\}/g, currentVpsMeasurements.VehicleMaxWidth.toFixed(2))
            .replace(/\{1\}/g, currentVpsMeasurements.VehicleMaxLength.toFixed(2))
            .replace(/\{2\}/g, currentVpsMeasurements.VehicleMaxHeight.toFixed(2))
            .replace(/\{3\}/g, currentVpsMeasurements.LmsDistanceWidth.toFixed(2))
            .replace(/\{4\}/g, currentVpsMeasurements.LmsDistanceLength.toFixed(2))
            .replace(/\{5\}/g, currentVpsMeasurements.LmsHeightWidth.toFixed(2))
            .replace(/\{6\}/g, currentVpsMeasurements.LmsHeightLength.toFixed(2));

        let pointDistance = { value: 0 };
        if (!this.IsMaxPointDistanceFrontRespected(pointDistance, currentVpsMeasurements)) {
            healthState.addClass("glyphicon-ok warning");
            validityStates.VpsMeasurements = true;
            //return Localization.resourceStrings.SiteWizardModal_WarningMaxPointDistanceFront
            //    .replace(/\{0\}/g, pointDistance.value.toFixed(2))
            //    .replace(/\{1\}/g, currentVpsMeasurements.MaxPointDistanceFront.toFixed(2)) +
            return "LmsLength has a distance of " + currentVpsMeasurements.LmsDistanceLength + " m to the portal. A maximum distance of 30 m is recommended." +
                "</br>" +
                settingsString;
        }

        let minAngles = { side: 0, top: 0, front: 0 };
        if (!this.IsMinObservationAngleRespected(minAngles, currentVpsMeasurements)) {
            healthState.addClass("glyphicon-ok warning");
            validityStates.VpsMeasurements = true;
            if (minAngles.side < currentVpsMeasurements.MinObservationAngle) {
                return Localization.resourceStrings.SiteWizardModal_WarningMeasurementsAngleSide
                    .replace(/\{0\}/g, minAngles.side.toFixed(1))
                    .replace(/\{1\}/g, currentVpsMeasurements.MinObservationAngle.toFixed(1)) +
                    "</br>" +
                    settingsString;
            }
            if (minAngles.top < currentVpsMeasurements.MinObservationAngle) {
                return Localization.resourceStrings.SiteWizardModal_WarningMeasurementsAngleTop
                    .replace(/\{0\}/g, minAngles.top.toFixed(1))
                    .replace(/\{1\}/g, currentVpsMeasurements.MinObservationAngle.toFixed(1)) +
                    "</br>" +
                    settingsString;
            }
            if (minAngles.front < currentVpsMeasurements.MinObservationAngle) {
                return Localization.resourceStrings.SiteWizardModal_WarningMeasurementsAngleFront
                    .replace(/\{0\}/g, minAngles.front.toFixed(1))
                    .replace(/\{1\}/g, currentVpsMeasurements.MinObservationAngle.toFixed(1)) +
                    "</br>" +
                    settingsString;
            }
        }

        healthState.addClass("glyphicon-ok good");
        validityStates.VpsMeasurements = true;
        return settingsString;
    }


    // ß: observationAngle (smallest angle on each side [here on the vehicle side])
    //     LMS     
    //        \     _______                                    
    //         \   |       |                              
    //          \  |Vehicle|
    //           \ß|       |                              
    //            \|       |                          
    private static IsMinObservationAngleRespected(minAngles: { side: number, top: number, front: number },
        vpsMeasurements: WizardVpsMeasurementsInfoViewModel): boolean {
        minAngles.side = Math.atan2((vpsMeasurements.LmsDistanceWidth - vpsMeasurements.VehicleMaxWidth) / 2,
            vpsMeasurements.LmsHeightWidth) *
            180 /
            Math.PI;
        minAngles.top = Math.atan2(vpsMeasurements.LmsHeightWidth - vpsMeasurements.VehicleMaxHeight,
            (vpsMeasurements.LmsDistanceWidth + vpsMeasurements.VehicleMaxWidth) / 2) *
            180 /
            Math.PI;
        minAngles.front = Math.atan2((vpsMeasurements.LmsDistanceLength - vpsMeasurements.VehicleMaxLength) / 2,
            vpsMeasurements.LmsHeightLength) *
            180 /
            Math.PI;

        return minAngles.side >= vpsMeasurements.MinObservationAngle &&
            minAngles.top >= vpsMeasurements.MinObservationAngle &&
            minAngles.front >= vpsMeasurements.MinObservationAngle;
    }

    // H = LmsHeightLength
    // L = LmsDistanceLength
    // ß = angleLmsToLowestPoint
    // x = distLmsToLowestPoint
    // pointDistance: if a laserray of the LMS Length would hit the ground in the profile width plane, this would be the distance to the closest point in that plane (next laserray according to Lms5xxAngularResolution) 
    //                      LMS
    //                     /|
    //                   //ß|
    //                 / /  |
    //                | /x  |H
    //  pointDistance |/__L_|
    private static IsMaxPointDistanceFrontRespected(pointDistance: { value: number },
        vpsMeasurements: WizardVpsMeasurementsInfoViewModel): boolean {
        // temporary condition until final condition is fixed
        return vpsMeasurements.LmsDistanceLength <= 30;
        //vpsMeasurements.Lms5xxAngularResolution = 0.5;
        //let distLmsToLowestPoint = Math.sqrt(Math.pow(vpsMeasurements.LmsHeightLength, 2) +
        //    Math.pow(vpsMeasurements.LmsDistanceLength, 2));
        //let angleLmsToLowestPoint = Math.asin(vpsMeasurements.LmsDistanceLength / distLmsToLowestPoint);
        //pointDistance.value = Math.sin(vpsMeasurements.Lms5xxAngularResolution * Math.PI / 180) /
        //    Math.sin(vpsMeasurements.Lms5xxAngularResolution * Math.PI / 180 + angleLmsToLowestPoint) *
        //    distLmsToLowestPoint

        //return pointDistance.value < vpsMeasurements.MaxPointDistanceFront;
    }

}
